import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import SEO from "@/components/SEO";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

import heroImage from "@/assets/ibiza-drive-hero.webp";
import driveImage from "@/assets/drive.webp";
import serviceImage from "@/assets/drive-huracan.webp";

const ease = [0.22, 1, 0.36, 1] as const;

const whyReasons = [
  {
    title: "A curated fleet",
    text: "We offer a carefully selected range of luxury and prestige vehicles, chosen for quality, comfort, and performance.",
  },
  {
    title: "Delivered to your location",
    text: "Vehicles are delivered directly to your hotel, residence, or arrival terminal, ready for immediate use.",
  },
  {
    title: "Flexible arrangements",
    text: "Whether for a single day or an extended period, each booking is tailored to your schedule.",
  },
  {
    title: "Seamless coordination",
    text: "From reservation to handover, every step is handled with precision and clarity.",
  },
  {
    title: "Personal service",
    text: "Our team remains available throughout your booking to assist with any adjustments or additional requests.",
  },
];

const steps = [
  { step: "1", title: "Request", text: "Share your preferred vehicle, location, and dates." },
  { step: "2", title: "Selection", text: "We present available options aligned with your preferences." },
  { step: "3", title: "Confirmation", text: "Once confirmed, the vehicle is secured and prepared." },
  { step: "4", title: "Delivery", text: "Your vehicle is delivered to your chosen location." },
  { step: "5", title: "Support", text: "Our team remains available throughout your rental period." },
];

const destinations = [
  { label: "Ibiza", path: "/drive/ibiza" },
  { label: "Marbella", path: "/drive/marbella" },
  { label: "Saint-Tropez", path: null },
  { label: "Dubai", path: "/dubai#services" },
  { label: "Paris", path: null },
];

const Drive = () => {
  const navigate = useNavigate();

  const handleDestinationClick = (dest: typeof destinations[0]) => {
    if (dest.path) {
      navigate(dest.path);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEO
        title="Arzani Drive — Prestige Vehicles Delivered to You"
        description="A curated fleet of luxury and performance vehicles delivered to your residence, hotel or arrival terminal."
        path="/drive"
      />
      <Navigation />

      {/* Hero */}
      <section className="relative h-screen flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImage} alt="Arzani Drive" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground/70 via-foreground/20 to-foreground/10" />
        </div>
        <div className="relative z-10 px-8 md:px-16 lg:px-20 pb-20 md:pb-28 max-w-2xl">
          <motion.span
            className="font-label text-primary-foreground/40 tracking-[0.3em] text-[9px] block mb-5"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 2, ease }}
          >
            Prestige Vehicles · Worldwide
          </motion.span>
          <motion.h1
            className="font-display text-4xl md:text-6xl lg:text-7xl text-primary-foreground leading-[1] tracking-wide uppercase mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 2, ease }}
          >
            Arzani Drive
          </motion.h1>
          <motion.p
            className="font-body text-primary-foreground/50 text-sm leading-[1.9] max-w-md"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.4, duration: 2, ease }}
          >
            Mobility defines how you experience a destination.
          </motion.p>
        </div>
      </section>

      {/* Opening Text */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-3xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">The Experience</span>
            <p className="font-body text-muted-foreground text-sm md:text-base leading-[1.9] mb-6">
              Through Arzani Drive, every journey is arranged with precision, offering effortless access to the places that matter most. From arrival to departure, each vehicle is selected to provide comfort, discretion, and flexibility throughout your stay.
            </p>
            <p className="font-body text-muted-foreground text-sm md:text-base leading-[1.9]">
              Whether navigating city streets or coastal roads, your movement remains seamless — allowing you to focus entirely on the experience ahead.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Destinations Bar */}
      <section className="pb-16 md:pb-24 bg-background">
        <div className="container-luxury">
          <motion.div
            className="border-y border-foreground/10 py-8"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze text-[10px] tracking-[0.3em] block text-center mb-6">
              Our Destinations
            </span>
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
              {destinations.map((dest) => (
                <button
                  key={dest.label}
                  onClick={() => handleDestinationClick(dest)}
                  disabled={!dest.path}
                  className={`px-6 md:px-8 py-2.5 text-[10px] md:text-xs tracking-[0.2em] uppercase font-label border transition-all duration-500 ${
                    dest.path
                      ? "border-foreground/20 text-foreground hover:bg-foreground hover:text-background cursor-pointer"
                      : "border-foreground/10 text-muted-foreground/40 cursor-default"
                  }`}
                >
                  {dest.label}
                  {!dest.path && <span className="ml-2 text-[8px] tracking-normal normal-case opacity-60">Coming soon</span>}
                </button>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Second Text + Image */}
      <section className="section-spacing bg-warm-black">
        <div className="container-luxury">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-center">
            <motion.div
              className="md:col-span-5"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.2, ease }}
            >
              <span className="font-label text-bronze block mb-6">Your Journey</span>
              <p className="font-body text-primary-foreground/60 text-sm md:text-base leading-[1.9] mb-6">
                Our selection includes a range of prestige vehicles, from refined daily drivers to high-performance models and executive transport. Each arrangement is tailored to your schedule, ensuring that your vehicle is available exactly when and where you need it.
              </p>
              <p className="font-body text-primary-foreground/60 text-sm md:text-base leading-[1.9]">
                From airport arrivals to full-day availability, every detail is handled in advance, creating a sense of ease and continuity throughout your stay.
              </p>
            </motion.div>
            <motion.div
              className="md:col-span-7"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.2, delay: 0.15, ease }}
            >
              <div className="aspect-[3/4] md:aspect-[4/5] overflow-hidden">
                <img src={serviceImage} alt="Prestige vehicle" className="w-full h-full object-cover" loading="lazy" />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Book with Arzani */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-5xl">
          <motion.div
            className="text-center mb-16 md:mb-24"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">Why Book With Arzani</span>
            <h2 className="font-display text-3xl md:text-5xl text-foreground tracking-wide uppercase">
              The Difference
            </h2>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-12 md:gap-x-16 md:gap-y-16">
            {whyReasons.map((reason, i) => (
              <motion.div
                key={reason.title}
                className="w-full md:w-[calc(50%-2rem)] lg:w-[calc(33.333%-2.67rem)]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: i * 0.1, ease }}
              >
                <h3 className="font-display-medium text-base text-foreground tracking-wide uppercase mb-3">
                  {reason.title}
                </h3>
                <div className="w-8 h-px bg-bronze mb-4" />
                <p className="font-body text-muted-foreground text-sm leading-[1.9]">
                  {reason.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-spacing bg-warm-black">
        <div className="container-luxury max-w-4xl">
          <motion.div
            className="text-center mb-16 md:mb-24"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">How It Works</span>
            <h2 className="font-display text-3xl md:text-5xl text-primary-foreground tracking-wide uppercase">
              Your Booking
            </h2>
          </motion.div>

          <div className="flex flex-wrap justify-center gap-12 md:gap-x-16 md:gap-y-16">
            {steps.map((item, i) => (
              <motion.div
                key={item.step}
                className="w-full md:w-[calc(50%-2rem)] lg:w-[calc(33.333%-2.67rem)] text-center"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: i * 0.1, ease }}
              >
                <span className="font-display text-5xl md:text-6xl text-primary-foreground/10 block mb-4">
                  {item.step}
                </span>
                <h3 className="font-display-medium text-lg text-primary-foreground tracking-wide uppercase mb-4">
                  {item.title}
                </h3>
                <p className="font-body text-primary-foreground/50 text-sm leading-[1.9]">
                  {item.text}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-6xl">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">FAQ</span>
            <h2 className="font-display text-3xl md:text-5xl text-foreground tracking-wide uppercase">
              Frequently Asked Questions
            </h2>
          </motion.div>

          {(() => {
            const faqs = [
              { q: "How does booking a vehicle through Arzani work?", a: "After submitting your request, we provide available vehicle options based on your preferences. Once confirmed, the vehicle is reserved and delivered to your desired location." },
              { q: "Where can the vehicle be delivered?", a: "Vehicles can be delivered to airports, hotels, private residences, or other agreed locations." },
              { q: "What types of vehicles are available?", a: "We offer a curated selection of luxury and prestige vehicles, including sports cars, SUVs, and executive models." },
              { q: "What is required to rent a vehicle?", a: "A valid driving license and identification are required. Additional requirements may vary depending on the provider." },
              { q: "Is a deposit required?", a: "Yes. A deposit is required to confirm the reservation. Details will be provided during the booking process." },
              { q: "Can I request a specific vehicle model?", a: "Yes. We will always aim to source your preferred model, subject to availability." },
              { q: "Is insurance included?", a: "Basic insurance is typically included. Additional coverage options may be available upon request." },
              { q: "Can I book a chauffeur with the vehicle?", a: "Yes. Chauffeur-driven services can be arranged as part of Arzani Drive or through Arzani Lifestyle." },
              { q: "What happens in case of cancellation?", a: "Cancellation policies vary depending on the provider and will be communicated before confirmation." },
              { q: "Can vehicles be arranged last-minute?", a: "Yes, subject to availability. Our team will always aim to accommodate urgent requests." },
            ];
            const left = faqs.slice(0, 5);
            const right = faqs.slice(5);
            return (
              <motion.div
                className="grid grid-cols-1 md:grid-cols-2 gap-x-12"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1, ease }}
              >
                <Accordion type="single" collapsible className="w-full">
                  {left.map((item, i) => (
                    <AccordionItem key={i} value={`faq-left-${i}`} className="border-foreground/10">
                      <AccordionTrigger className="font-display-medium text-sm text-foreground tracking-wide uppercase text-left hover:no-underline py-5">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="font-body text-muted-foreground text-sm leading-[1.8]">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
                <Accordion type="single" collapsible className="w-full">
                  {right.map((item, i) => (
                    <AccordionItem key={i} value={`faq-right-${i}`} className="border-foreground/10">
                      <AccordionTrigger className="font-display-medium text-sm text-foreground tracking-wide uppercase text-left hover:no-underline py-5">
                        {item.q}
                      </AccordionTrigger>
                      <AccordionContent className="font-body text-muted-foreground text-sm leading-[1.8]">
                        {item.a}
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </motion.div>
            );
          })()}

          <motion.div
            className="text-center mt-16"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2, ease }}
          >
            <p className="font-body text-muted-foreground/60 text-sm mb-6">Still have a question?</p>
            <a
              href="https://wa.me/message/2XEKJ5STR2IZJ1"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
            >
              Contact Us
            </a>
          </motion.div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="section-spacing bg-background">
        <div className="container-luxury max-w-2xl text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1.2, ease }}
          >
            <span className="font-label text-bronze block mb-6">Get In Touch</span>
            <h2 className="font-display text-3xl md:text-4xl text-foreground tracking-wide uppercase mb-6">
              Request a Vehicle
            </h2>
            <p className="font-body text-muted-foreground text-sm leading-[1.9] mb-10 max-w-lg mx-auto">
              Send us a message with your preferred vehicle, dates, and delivery location.
              Our team will respond promptly with availability and a tailored proposal.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://wa.me/message/2XEKJ5STR2IZJ1"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
              >
                WhatsApp
              </a>
              <a
                href="mailto:info@arzanigroupworldwide.com"
                className="inline-block px-10 py-3 border border-foreground/20 text-foreground text-xs tracking-[0.2em] uppercase font-label hover:bg-foreground hover:text-background transition-colors duration-500"
              >
                Email Us
              </a>
            </div>
            <p className="font-body text-muted-foreground/60 text-xs mt-8">
              +31 6 19038233 · info@arzanigroupworldwide.com
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Drive;
